import React from 'react';
import { Navigate } from 'react-router-dom';
import Loader from '@/app/routes/Loader';
import ErrorPage403 from '@/app/routes/errorPage403';
import { Textarea, TextField } from '@/shared/components/TextField';
import { ToggleButton } from '@shared/components/ToggleButton';
import { Tooltip } from '@shared/components/Tooltip';
import { Snackbar } from '@shared/components/Snackbar';
import CreateLayout from '@/shared/layout/createLayout';
import UpdateStatusDialog from '@/epics/adminManagment/pages/adminList/UpdateStatusDialog';
import { useUserPrivileges } from '@shared/hooks/useUserPrivileges';
import TagInput from '../../components/TagInput/TagInput';
import { QMS_CATEGORY_LABELS } from '../../constants/labels';
import { QMS_CATEGORY_ERROR_MESSAGES } from '../../errors/errorMessages';
import { useEditQmsCategory } from '../../hooks/useEditQmsCategory';
import { useUpdateQmsCategoryStatus } from '../../hooks/useUpdateQmsCategoryStatus';
import { getQmsCategoryStatusDialogConfig } from '../categoryList/qmsCategoryStatusDialogConfig';
import ViewQmsCategoryErrorScreen from '../viewCategory/ViewQmsCategoryErrorScreen';

const EditQmsCategory: React.FC<{ categoryId: string }> = ({ categoryId }) => {
  const {
    formData,
    status,
    errors,
    tagError,
    isLoading,
    isSubmitting,
    loadError,
    notFound,
    accessError,
    hasChanges,
    isCancelModalOpen,
    isSnackbarOpen,
    snackbarError,
    handleNameChange,
    handleDescriptionChange,
    handleTagsChange,
    handleTagPendingChange,
    handleStatusToggle,
    handleSubmit,
    handleCancel,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleCloseSnackbar,
    tagSuggestions,
    isTagSuggestionsLoading,
    onTagSearchChange,
  } = useEditQmsCategory(categoryId);

  const {
    selectedCategory: statusTarget,
    isDialogOpen: isStatusDialogOpen,
    isSubmitting: isStatusSubmitting,
    handleOpenDialog: handleOpenStatusDialog,
    handleDialogClose: handleStatusDialogClose,
    handleConfirm: handleStatusConfirm,
    showSuccessSnackbar: showStatusSuccessSnackbar,
    successMessage: statusSuccessMessage,
    setShowSuccessSnackbar: setShowStatusSuccessSnackbar,
    showErrorSnackbar: showStatusErrorSnackbar,
    errorMessage: statusErrorMessage,
    setShowErrorSnackbar: setShowStatusErrorSnackbar,
  } = useUpdateQmsCategoryStatus({ onSuccess: handleStatusToggle });

  const { privileges, isLoading: isPrivilegesLoading } = useUserPrivileges();

  if (!isPrivilegesLoading && !privileges.qmsDocCategoryEdit) {
    return <ErrorPage403 />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center bg-white h-screen w-full">
        <Loader />
      </div>
    );
  }

  if (accessError) {
    return <ErrorPage403 />;
  }

  if (notFound) {
    return <Navigate to="/404" replace />;
  }

  if (loadError) {
    return <ViewQmsCategoryErrorScreen />;
  }

  const statusLabel =
    status === 'active'
      ? QMS_CATEGORY_LABELS.STATUS_ACTIVE
      : QMS_CATEGORY_LABELS.STATUS_INACTIVE;

  const Header = () => (
    <div className="flex items-center justify-between">
      <p className="text-titleLarge text-neutral-10">
        {QMS_CATEGORY_LABELS.EDIT_TITLE}
      </p>
      <div
        className="flex items-center gap-2 mr-5"
        data-testid="edit-status-toggle"
      >
        <p className="text-[12px] text-on-surface-variant">
          {QMS_CATEGORY_LABELS.STATUS_LABEL}
        </p>
        {privileges.qmsDocCategoryActivateDeactivate ? (
          <div
            className="inline-flex cursor-pointer"
            data-testid="toggle-wrapper"
            onClick={
              isSubmitting
                ? undefined
                : () =>
                    handleOpenStatusDialog({
                      id: categoryId,
                      name: formData.name,
                      status,
                    })
            }
            onMouseDown={(e) => e.preventDefault()}
            style={isSubmitting ? { pointerEvents: 'none' } : undefined}
          >
            <div style={{ pointerEvents: 'none' }}>
              <ToggleButton
                checked={status === 'active'}
                onChange={() => {}}
                label={statusLabel}
                size="XSmall"
                disabled={isSubmitting}
              />
            </div>
          </div>
        ) : (
          <Tooltip
            content={QMS_CATEGORY_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP}
            side="bottom"
            forceShow
            triggerWrapContent
          >
            <div
              className="inline-flex cursor-not-allowed opacity-50"
              data-testid="toggle-wrapper"
              onMouseDown={(e) => e.preventDefault()}
            >
              <div style={{ pointerEvents: 'none' }}>
                <ToggleButton
                  checked={status === 'active'}
                  onChange={() => {}}
                  label={statusLabel}
                  size="XSmall"
                  disabled
                />
              </div>
            </div>
          </Tooltip>
        )}
      </div>
    </div>
  );

  return (
    <>
      <CreateLayout
        isSubmitting={isSubmitting}
        handleSubmit={handleSubmit}
        handleCancel={handleCancel}
        isCancelModalOpen={isCancelModalOpen}
        handleCloseCancelModal={handleCloseCancelModal}
        handleConfirmCancel={handleConfirmCancel}
        isSnackbarOpen={isSnackbarOpen}
        handleCloseSnackbar={handleCloseSnackbar}
        snackbarError={snackbarError}
        Header={Header}
        submitButtonLabel={QMS_CATEGORY_LABELS.BUTTON_SAVE}
        submitButtonLoadingLabel={QMS_CATEGORY_LABELS.BUTTON_SAVE_LOADING}
        cancelButtonLabel={QMS_CATEGORY_LABELS.BUTTON_CANCEL}
        submitButtonDisabled={isSubmitting || !hasChanges}
      >
        <div className="flex flex-col gap-2">
          <p className="text-[18px] text-on-surface-variant">
            {QMS_CATEGORY_LABELS.SECTION_DETAILS}
          </p>
          <div className="flex flex-col gap-8">
            <div className="flex gap-4 w-[50%]">
              <TextField
                type="text"
                inputMode="text"
                label={QMS_CATEGORY_LABELS.FIELD_NAME}
                maxLength={50}
                autoFocus
                fullWidth
                autoComplete="off"
                value={formData.name}
                onChange={handleNameChange}
                error={Boolean(errors.name)}
                errorMessage={errors.name ?? ''}
                disabled={isSubmitting}
              />
            </div>
            <TagInput
              className="w-[calc(50%+5.25rem)]"
              existingTags={formData.existingTags}
              newTags={formData.newTags}
              onChange={handleTagsChange}
              disabled={isSubmitting}
              suggestions={tagSuggestions}
              isSuggestionsLoading={isTagSuggestionsLoading}
              onSearchChange={onTagSearchChange}
              error={tagError}
              onPendingChange={handleTagPendingChange}
            />
            <Textarea
              label={QMS_CATEGORY_LABELS.FIELD_DESCRIPTION}
              maxLength={500}
              fullWidth
              rows={4}
              value={formData.description}
              onChange={handleDescriptionChange}
              error={Boolean(errors.description)}
              errorMessage={errors.description ?? ''}
              disabled={isSubmitting}
            />
          </div>
        </div>
      </CreateLayout>

      {statusTarget && (
        <UpdateStatusDialog
          open={isStatusDialogOpen}
          onClose={handleStatusDialogClose}
          onConfirm={handleStatusConfirm}
          isSubmitting={isStatusSubmitting}
          {...getQmsCategoryStatusDialogConfig(
            statusTarget.name,
            statusTarget.status === 'active'
          )}
        />
      )}

      <Snackbar
        open={showStatusSuccessSnackbar}
        message={statusSuccessMessage}
        variant="success"
        onClose={() => setShowStatusSuccessSnackbar(false)}
        autoHideDuration={5000}
        position="top-center"
        showCloseButton={true}
      />

      <Snackbar
        open={showStatusErrorSnackbar}
        message={statusErrorMessage}
        variant="error"
        onClose={() => setShowStatusErrorSnackbar(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton={true}
      />
    </>
  );
};

export default EditQmsCategory;
